import { useEffect, useState, type FormEvent } from 'react';
import { emailProblem, emailSuggestion } from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { clearReferral, pendingReferral } from '../lib/referral';
import { Link, navigate } from '../lib/router';
import { login, register, useMe } from '../stores/session';
import { AuthLayout } from '../components/AuthLayout';
import {
  errorMessage,
  Field,
  Heading,
  PASSWORD_RULE,
  PasswordField,
  passwordProblem,
  PrimaryButton,
} from '../components/AuthForm';
import { OtpStep, useNext } from '../components/OtpStep';

/** 0 = doesn't meet the policy; 1–4 = meets it, plus a point each for mixed case, a symbol and 12+ characters. */
function strength(pw: string) {
  if (passwordProblem(pw)) return 0;
  return 1 + Number(/[a-z]/.test(pw) && /[A-Z]/.test(pw)) + Number(/[^A-Za-z0-9]/.test(pw)) + Number(pw.length >= 12);
}
const STRENGTH = [
  { label: '', color: 'bg-line', text: 'text-down' },
  { label: 'Fair — add upper case, a symbol or more length', color: 'bg-warn', text: 'text-warn' },
  { label: 'Good', color: 'bg-up', text: 'text-up' },
  { label: 'Strong', color: 'bg-up', text: 'text-up' },
  { label: 'Very strong', color: 'bg-up', text: 'text-up' },
];

export function RegisterPage() {
  const me = useMe();
  const next = useNext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [refCode, setRefCode] = useState(() => pendingReferral() ?? '');
  /** Arrived by a friend's link: the code is filled in and locked, with who invited them. */
  const [invite, setInvite] = useState<{ state: 'checking' | 'ok' | 'unknown'; username?: string } | null>(() => (pendingReferral() ? { state: 'checking' } : null));
  /** Set once the account exists, so a retry after a failed login step doesn't register twice. */
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<{ challengeId: string; method: 'email' | 'totp'; devCode?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; exists: boolean } | null>(null);

  // Signed in (also right after creating the account): continue with details and documents.
  useEffect(() => {
    if (me) navigate(me.onboardingStep === 'done' ? next : '/onboarding', { replace: true });
  }, [me, next]);

  useEffect(() => {
    const code = pendingReferral();
    if (!code) return;
    let live = true;
    api
      .get<{ username: string }>(`/api/referrals/lookup?code=${encodeURIComponent(code)}`)
      .then((r) => live && setInvite({ state: 'ok', username: r.username }))
      .catch((err) => {
        if (!live) return;
        // Not found: let them sign up without it, or type another. Anything else: keep the code, sign-up checks it.
        if (err instanceof ApiError && err.status === 404) {
          clearReferral();
          setRefCode('');
          setInvite({ state: 'unknown' });
        } else setInvite({ state: 'ok' });
      });
    return () => {
      live = false;
    };
  }, []);

  function dropInvite() {
    clearReferral();
    setRefCode('');
    setInvite(null);
  }

  const score = strength(password);
  const [emailTouched, setEmailTouched] = useState(false);
  const emailError = emailProblem(email);
  const suggestion = emailError ? null : emailSuggestion(email);
  const canSubmit = !emailError && !passwordProblem(password) && agreed && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const address = email.trim();
    setBusy(true);
    setError(null);
    try {
      if (registeredEmail !== address) {
        await register(address, password, refCode.trim() || undefined);
        setRegisteredEmail(address);
      }
      // Without OTP (dev) this logs straight in and the page redirects via `me`.
      const res = await login(address, password);
      // A new sign-up is a client: never the staff Telegram code.
      if (res.otpRequired && res.method !== 'telegram') setChallenge({ challengeId: res.challengeId, method: res.method ?? 'email', devCode: res.devCode });
    } catch (err) {
      setError({ message: errorMessage(err), exists: err instanceof ApiError && err.status === 409 });
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <AuthLayout title="Verify your email">
        <OtpStep
          email={email.trim()}
          challengeId={challenge.challengeId}
          method={challenge.method}
          devCode={challenge.devCode}
          title="Verify your email"
          subtitle="One last step"
          backLabel="Back"
          next="/onboarding"
          onResend={async () => {
            const res = await login(email.trim(), password);
            if (res.otpRequired && res.method !== 'telegram') setChallenge({ challengeId: res.challengeId, method: res.method ?? 'email', devCode: res.devCode });
          }}
          onBack={() => setChallenge(null)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign up" legalFooter={false}>
      <div className="mb-8" aria-label="Step 1 of 4">
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`h-1 flex-1 rounded-full ${i === 0 ? 'bg-fg' : 'bg-line'}`} />
          ))}
        </div>
        <div className="mt-2.5 text-[12.5px] font-medium text-muted">
          Step 1 of 4 · <span className="text-fg">Create your login</span>
        </div>
      </div>
      <Heading title="Create your account" subtitle="Trade in minutes" />
      <p className="mt-3.5 text-[15px] text-muted">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-accent-fg">
          Log in →
        </Link>
      </p>

      {invite?.state === 'ok' && invite.username && (
        <div className="mt-6 flex items-center gap-3 rounded-[18px] border border-up/40 bg-up-bg px-4 py-3">
          <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-full bg-up text-[15px] font-semibold uppercase text-white">
            {invite.username.charAt(0)}
          </span>
          <span className="min-w-0 text-[14px] leading-snug">
            <b className="break-all">{invite.username}</b> invited you
            <span className="block text-[13px] text-muted">Their referral code is applied automatically.</span>
          </span>
        </div>
      )}

      <ul className="mt-6 grid gap-2 text-sm text-muted">
        {['Create your login, then verify your identity in about 2 minutes', 'Your account is reviewed and opened within 24 hours'].map((t) => (
          <li key={t} className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-up">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {t}
          </li>
        ))}
      </ul>

      <form onSubmit={submit} noValidate className="mt-7">
        <Field
          id="email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          onBlur={() => setEmailTouched(true)}
          error={emailTouched && email.trim() ? emailError : null}
          autoFocus
        />
        {suggestion && (
          <p className="mx-6 -mt-1.5 mb-3 text-[13px] text-muted">
            Did you mean{' '}
            <button type="button" onClick={() => setEmail(suggestion)} className="font-semibold text-accent-fg underline underline-offset-4">
              {suggestion}
            </button>
            ?
          </p>
        )}
        <PasswordField id="password" label="Password" autoComplete="new-password" value={password} onChange={setPassword} />

        <div className="mx-1 mb-5 mt-2" aria-live="polite">
          <div className="flex gap-1.5">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= score ? STRENGTH[score]!.color : 'bg-line'}`}
              />
            ))}
          </div>
          <p className={`mt-1.5 text-[13px] ${password ? STRENGTH[score]!.text : 'text-muted'}`}>
            {!password ? PASSWORD_RULE : score === 0 ? passwordProblem(password) : STRENGTH[score]!.label}
          </p>
        </div>

        {invite && invite.state !== 'unknown' ? (
          <div className="mb-3 flex h-14 items-center justify-between gap-3 rounded-full border border-accent bg-accent-bg pl-6 pr-5">
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="text-[12px] text-muted">Referral code</span>
              <span id="ref" className="num truncate text-[15px] font-medium">
                {refCode}
              </span>
            </span>
            <button type="button" onClick={dropInvite} className="shrink-0 text-[13px] font-semibold text-accent-fg">
              Not your code?
            </button>
          </div>
        ) : (
          <Field id="ref" label="Referral code (optional)" autoComplete="off" value={refCode} onChange={(v) => setRefCode(v.replace(/[^A-Za-z0-9_]/g, '').slice(0, 16))} />
        )}
        {invite?.state === 'unknown' && <p className="mx-1 -mt-1 mb-3 text-[13px] text-muted">The code in your invite link wasn't found. You can sign up without it or enter another code.</p>}

        <label className="mx-1 mb-[22px] flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-muted">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-[3px] size-4 shrink-0 accent-[var(--accent)]"
          />
          <span className="[&_a]:font-medium [&_a]:text-fg [&_a]:underline">
            I am 18 or older, I understand that CFDs are high-risk leveraged products, and I agree to the{' '}
            <Link to="/terms" target="_blank" rel="noopener">Terms of Use</Link>, <Link to="/privacy" target="_blank" rel="noopener">Privacy Notice</Link> and{' '}
            <Link to="/risk-disclosure" target="_blank" rel="noopener">Risk Disclosure</Link>.
          </span>
        </label>

        {error && (
          <div role="alert" className="mb-3 rounded-2xl bg-down-bg px-4 py-3 text-sm text-down">
            {error.message}
            {error.exists && (
              <>
                {' '}
                <Link to="/login" className="font-semibold underline">
                  Log in instead
                </Link>
              </>
            )}
          </div>
        )}
        <PrimaryButton disabled={!canSubmit} busy={busy} busyLabel="Creating account…">
          Create account
        </PrimaryButton>
      </form>

    </AuthLayout>
  );
}
